import {
  Button,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import { LoadingButton } from "src/grading/components";
import { InputError } from "src/grading/components/InputError";
import RFModal from "src/grading/components/RFModal/RFModal";
import { useCreateOrgMutation } from "src/store/reducers/organizations/organizationsApiSlice";
import theme from "src/theme";
import * as Yup from "yup";
import useStyles from "./CreateOrgModal.styles";

interface CreateOrgModalProps {
  onClose: () => void;
  open: boolean;
  fetchOrganizations: () => Promise<void>;
}

export interface CreateOrgFormValues {
  name: string;
  branch: string;
  ownerFirstName: string;
  ownerLastName: string;
  ownerEmail: string;
}

const CreateOrgModal = ({
  onClose,
  open,
  fetchOrganizations,
}: CreateOrgModalProps) => {
  const classes = useStyles();

  const [createOrg, { isLoading }] = useCreateOrgMutation();

  const formik = useFormik({
    initialValues: {
      name: "",
      branch: "",
      ownerFirstName: "",
      ownerLastName: "",
      ownerEmail: "",
    },
    validationSchema: Yup.object().shape({
      name: Yup.string().required(),
      branch: Yup.string().required(),
      ownerFirstName: Yup.string().required(),
      ownerLastName: Yup.string().required(),
      ownerEmail: Yup.string()
        .email("Enter a valid email")
        .required("Enter a valid email"),
    }),
    onSubmit: async (values: CreateOrgFormValues, { resetForm }) => {
      try {
        const res = await createOrg({ payload: values }).unwrap();

        if (res.success) {
          await fetchOrganizations();
          resetForm();
          onClose();
        }
      } catch (error: any) {
        alert(error.data.message);
      }
    },
  });

  const {
    values,
    handleSubmit,
    handleChange,
    setFieldValue,
    resetForm,
    touched,
    errors,
  } = formik;

  return (
    <RFModal
      open={open}
      boxProps={{ className: classes.container }}
      onClose={onClose}
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Grid
        container
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleSubmit();
        }}
        rowGap={theme.spacing(3)}
      >
        <Grid item xs={12}>
          <Typography variant="h5">Create New Organization</Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="body1">
            Set up a new organization with basic information
          </Typography>
        </Grid>
        <Grid container>
          <Grid container>
            <Typography
              variant="subtitle2"
              fontWeight="bold"
              color="black"
              textTransform="uppercase"
            >
              Organization Name
            </Typography>
          </Grid>
          <Grid container>
            <TextField
              fullWidth
              variant="outlined"
              value={values.name}
              placeholder="Organization Name"
              onChange={(e) => handleChange("name")(e)}
              error={InputError("name", touched, errors).error}
              helperText={InputError("name", touched, errors).helperText}
            />
          </Grid>
        </Grid>
        <Grid container>
          <Grid container>
            <Typography
              variant="subtitle2"
              fontWeight="bold"
              color="black"
              textTransform="uppercase"
            >
              Branch
            </Typography>
          </Grid>
          <Grid container>
            <Select
              fullWidth
              variant="outlined"
              value={values.branch}
              onChange={(e) => {
                setFieldValue("branch", e.target.value);
              }}
              error={!!errors.branch}
            >
              <MenuItem value="army">Army</MenuItem>
              <MenuItem value="navy">Navy</MenuItem>
              <MenuItem value="airforce">Air Force</MenuItem>
              <MenuItem value="marine">Marine Corps</MenuItem>
              <MenuItem value="all">All</MenuItem>
            </Select>
          </Grid>
        </Grid>
        <Grid container>
          <Grid container>
            <Typography
              variant="subtitle2"
              fontWeight="bold"
              color="black"
              textTransform="uppercase"
            >
              Owner First Name
            </Typography>
          </Grid>
          <Grid container>
            <TextField
              fullWidth
              variant="outlined"
              value={values.ownerFirstName}
              placeholder="Owner First Name"
              onChange={(e) => handleChange("ownerFirstName")(e)}
              error={InputError("ownerFirstName", touched, errors).error}
              helperText={
                InputError("ownerFirstName", touched, errors).helperText
              }
            />
          </Grid>
        </Grid>
        <Grid container>
          <Grid container>
            <Typography
              variant="subtitle2"
              fontWeight="bold"
              color="black"
              textTransform="uppercase"
            >
              Owner Last Name
            </Typography>
          </Grid>
          <Grid container>
            <TextField
              fullWidth
              variant="outlined"
              value={values.ownerLastName}
              placeholder="Owner Last Name"
              onChange={(e) => handleChange("ownerLastName")(e)}
              error={InputError("ownerLastName", touched, errors).error}
              helperText={
                InputError("ownerLastName", touched, errors).helperText
              }
            />
          </Grid>
        </Grid>
        <Grid container>
          <Grid container>
            <Typography
              variant="subtitle2"
              fontWeight="bold"
              color="black"
              textTransform="uppercase"
            >
              Owner Email
            </Typography>
          </Grid>
          <Grid container>
            <TextField
              fullWidth
              variant="outlined"
              value={values.ownerEmail}
              placeholder="Owner Email"
              onChange={(e) => handleChange("ownerEmail")(e)}
              error={InputError("ownerEmail", touched, errors).error}
              helperText={InputError("ownerEmail", touched, errors).helperText}
            />
          </Grid>
        </Grid>
        <Grid
          container
          item
          xs={12}
          justifyContent="space-between"
          sx={{ alignSelf: "flex-end" }}
        >
          <Grid item>
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => {
                resetForm();
                onClose();
              }}
            >
              Cancel
            </Button>
          </Grid>
          <Grid item>
            <LoadingButton
              variant="contained"
              color="primary"
              label="Create Org"
              onClick={() => handleSubmit()}
              loading={isLoading}
            />
          </Grid>
        </Grid>
      </Grid>
    </RFModal>
  );
};

export default CreateOrgModal;
